import { Card, Grid, Icon, IconButton, InputLabel, Tooltip, Autocomplete } from "@mui/material";
import MDInput from "components/MDInput";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import DataTable from "examples/Tables/DataTable";
import DefaultCell from "layouts/dashboards/sales/components/DefaultCell";
import { useCallback, useEffect, useRef, useState } from "react";
import { verifyVariable } from "utils/Functions";
import { post } from "utils/WebService";
import StatusFichaCreateEdit from "./StatusFichaCreateEdit";

export default function StatusFichaConsult() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const tableState = useRef({
        pageSize: 10,
        pageIndex: 0,
        sorting: [],
        filtered: [{ id: 'cvAtivo', value: true }],
    });

    const [data, setData] = useState([]);
    const [pages, setPages] = useState(0);
    const [loadingTable, setLoadingTable] = useState(true);
    const [isCreateEdit, setIsCreateEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [recordsTotal, setRecordsTotal] = useState(0);
    const [aplicar, setAplicar] = useState(false);

    const [filters, setFilters] = useState({
        ccNome: '',
        cvAtivo: true,
        forca: '',
    });

    useEffect(() => {
        fetchData({ ...tableState });
    }, []);

    const fetchData = useCallback(({ pageSize, pageIndex, sorting, filtered }) => {
        setLoadingTable(true);
        const updatedTableState = {
            pageSize: pageSize ? pageSize : tableState.current.pageSize,
            pageIndex: (pageIndex == 0 || pageIndex) ? pageIndex : tableState.current.pageIndex,
            sorting: sorting ? sorting : tableState.current.sorting,
            filtered: filtered ? filtered : tableState.current.filtered,
        };
        tableState.current = updatedTableState;

        post("/status-ficha/list", {
            page: updatedTableState.pageIndex,
            pageSize: updatedTableState.pageSize,
            sorted: updatedTableState.sorting,
            filtered: updatedTableState.filtered,
        }).then(({ data, pages, recordsTotal }) => {
            setPages(pages);
            setData(data);
            setRecordsTotal(recordsTotal);
            setLoadingTable(false);
            setAplicar(false);
        }).catch(() => {
            setLoadingTable(false)
            setAplicar(false);
        });
    }, []);

    const backFunction = () => {
        fetchData({ ...tableState });
        setIsCreateEdit(false);
        setSelectedId(null);
    }

    const userColumns = [
        {
            header: "nome",
            accessorKey: "ccNome",
            cell: info => <DefaultCell>{info.getValue() || ""}</DefaultCell>,
            isSorted: true
        },
        {
            header: "ativo",
            accessorKey: "cvAtivo",
            cell: info => <DefaultCell>{info.getValue() ? "Sim" : "Não"}</DefaultCell>,
            isSorted: false
        },
        {
            header: "força/peso",
            accessorKey: "forca",
            cell: info => {
                const val = info.getValue();
                let label = "Não configurado";
                if (val === 1) label = "Fraco";
                if (val === 2) label = "Médio";
                if (val === 3) label = "Forte";
                return <DefaultCell>{label}</DefaultCell>;
            },
            isSorted: true
        },
        {
            header: "ações",
            isSorted: false,
            cell: info => (
                <Tooltip title="Editar">
                    <IconButton
                        onClick={() => {
                            console.log("DADOS DA LINHA:", info.row.original);
                            const rowId = info.row.original.id !== undefined ? info.row.original.id : info.row.original.cvId;
                            if (rowId !== undefined && rowId !== null) {
                                setSelectedId(rowId);
                                setIsCreateEdit(true);
                            } else {
                                alert("Erro: O ID não foi retornado pelo servidor. Verifique o console.");
                            }
                        }}>
                        <MDTypography variant="h4">
                            <Icon color="inherit" sx={{ marginRight: 1 }}>edit</Icon>
                        </MDTypography>
                    </IconButton>
                </Tooltip>
            ),
            width: "15%"
        },
    ];

    const handleApplyFilter = () => {
        let filtered = [];
        Object.keys(filters).forEach(filterKey => {
            if (verifyVariable(filters[filterKey])) {
                filtered.push({ id: filterKey, value: filters[filterKey] });
            }
        })
        setAplicar(true);
        fetchData({ ...tableState, pageIndex: 0, filtered });
    };

    const handleCleanFilter = () => {
        setFilters({ ccNome: '', cvAtivo: true, forca: '' });
        fetchData({ pageSize: 10, pageIndex: 0, sorting: [], filtered: [{ id: 'cvAtivo', value: true }] });
    };

    return (
        <Grid container display="flex" justifyContent="center">
            {isCreateEdit &&
                <Grid item xs={12} md={12}>
                    <StatusFichaCreateEdit id={selectedId} closeCreateEdit={backFunction} />
                </Grid>
            }
            <Grid item xs={12} md={12} sx={{ display: isCreateEdit ? "none" : "" }}>
                <Card variant="outlined" sx={{ px: 2, py: 1.5, mb: 2, background: darkMode ? "#23233a" : "#fafafa", borderColor: darkMode ? "#333" : "#ddd" }}>
                    <Grid container spacing={1} alignItems="flex-end">
                        <Grid item xs={12} sm={3}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Nome</InputLabel>
                            <MDInput size="small" placeholder="Nome..." value={filters.ccNome} onChange={({ target }) => setFilters({ ...filters, ccNome: target.value })} onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilter(); }} fullWidth />
                        </Grid>
                        <Grid item xs={12} sm={2.5}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Status</InputLabel>
                            <Autocomplete
                                options={[{ label: 'Ambos', value: '' }, { label: 'Ativos', value: true }, { label: 'Inativos', value: false }]}
                                value={[{ label: 'Ambos', value: '' }, { label: 'Ativos', value: true }, { label: 'Inativos', value: false }].find(opt => opt.value === filters.cvAtivo) || { label: 'Ambos', value: '' }}
                                onChange={(event, newValue) => {
                                    setFilters({ ...filters, cvAtivo: newValue ? newValue.value : '' });
                                }}
                                isOptionEqualToValue={(option, value) => option.value === value?.value}
                                renderInput={(params) => <MDInput {...params} size="small" placeholder="Status..." />}
                                disableClearable
                            />
                        </Grid>
                        <Grid item xs={12} sm={2.5}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Força/Peso</InputLabel>
                            <Autocomplete
                                options={[
                                    { label: 'Todos', value: '' },
                                    { label: 'Fraco', value: 1 },
                                    { label: 'Médio', value: 2 },
                                    { label: 'Forte', value: 3 }
                                ]}
                                value={[
                                    { label: 'Todos', value: '' },
                                    { label: 'Fraco', value: 1 },
                                    { label: 'Médio', value: 2 },
                                    { label: 'Forte', value: 3 }
                                ].find(opt => opt.value === filters.forca) || { label: 'Todos', value: '' }}
                                onChange={(event, newValue) => {
                                    setFilters({ ...filters, forca: newValue ? newValue.value : '' });
                                }}
                                isOptionEqualToValue={(option, value) => option.value === value?.value}
                                renderInput={(params) => <MDInput {...params} size="small" placeholder="Força/Peso..." />}
                                disableClearable
                            />
                        </Grid>
                        <Grid item xs={12} sm={4} display="flex" alignItems="center" justifyContent="space-between">
                            <MDBox>
                                <MDButton size="medium" variant="contained" color="info" onClick={() => { setSelectedId(null); setIsCreateEdit(true); }}>Adicionar</MDButton>
                            </MDBox>
                            <MDBox>
                                <MDButton size="medium" sx={{ marginRight: 2 }} variant="contained" color="info" onClick={handleCleanFilter}>Limpar filtro</MDButton>
                                <MDButton size="medium" variant="contained" color="info" onClick={handleApplyFilter}>Aplicar filtro</MDButton>
                            </MDBox>
                        </Grid>
                    </Grid>
                </Card>
                <DataTable loading={loadingTable} columnData={userColumns} data={data} fetchData={fetchData} totalPages={pages} totalRecords={recordsTotal} changeFilter={aplicar} tableState={tableState} />
            </Grid>
        </Grid>
    );
}
